import {
  useQuery,
  useMutation,
  useQueryClient,
  UseQueryResult,
  UseMutationResult,
} from '@tanstack/react-query'
import { requestV3 } from 'Features/Auth/axios-client'
import {
  ServiceLevelData,
  UpdateServiceLevelPayload,
} from './ServiceLevel.types'

const fetchServiceLevel = async (projectId: string, scenarioId: string) => {
  const { data } = await requestV3({
    url: `/service-level?projectId=${projectId}&scenarioId=${scenarioId}`,
  })
  return data
}

const updateServiceLevel = async (
  projectId: string,
  scenarioId: string,
  payload: UpdateServiceLevelPayload[]
) => {
  const { data } = await requestV3({
    url: `/service-level?projectId=${projectId}&scenarioId=${scenarioId}`,
    method: 'PUT',
    data: payload,
  })
  return data
}

export const useServiceLevel = ({
  projectId,
  scenarioId,
}: {
  projectId: string
  scenarioId: string
}): UseQueryResult<UpdateServiceLevelPayload[]> => {
  return useQuery({
    queryKey: ['service-level', projectId, scenarioId],
    queryFn: () => fetchServiceLevel(projectId, scenarioId),
    select: (data) => data.data,
    refetchOnWindowFocus: false,
    gcTime: Infinity,
  })
}

export const useUpdateServiceLevel = ({
  projectId,
  scenarioId,
  onSuccess,
  onError,
}: {
  projectId: string
  scenarioId: string
  onSuccess?: () => void
  onError?: (error: Error) => void
}): UseMutationResult<
  { data: ServiceLevelData[] },
  Error,
  UpdateServiceLevelPayload
> => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (payload: UpdateServiceLevelPayload[]) =>
      updateServiceLevel(projectId, scenarioId, payload),
    onSuccess: () => {
      // Invalidate and refetch service level data
      queryClient.invalidateQueries({
        queryKey: ['service-level', projectId, scenarioId],
      })
      // Call the success callback if provided
      onSuccess?.()
    },
    onError: (error: Error) => {
      // Call the error callback if provided
      onError?.(error)
    },
  })
}
